import { z } from 'zod';

/**
 * Version of the Terms of Service and Privacy Policy a person must accept (PRD 13). Bump it when either page
 * changes in a way people must agree to again; every account is then asked once more on its next visit.
 */
export const TERMS_VERSION = '2026-11-04';

/** Minimum age to hold an account (Terms, section 2). */
export const MINIMUM_AGE = 13;

/**
 * Countries whose law needs opt-in consent before analytics storage or tracking (GDPR and ePrivacy: the EU, the
 * rest of the EEA, the UK and Switzerland). Everywhere else gets notice and an opt-out in Settings, with no banner.
 */
const CONSENT_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU
  'IS', 'LI', 'NO', // EEA
  'GB', 'CH',
]);

/** True when the visitor's country needs an opt-in banner. Unknown countries (`null`, `XX`, Tor `T1`) count as needing it. */
export function consentRequired(country: string | null | undefined): boolean {
  if (!country || country === 'XX' || country === 'T1') return true;
  return CONSENT_COUNTRIES.has(country.toUpperCase());
}

/** A person's analytics choice: null = not asked (the country default applies). */
export const AnalyticsConsentSchema = z.enum(['granted', 'denied']);
export type AnalyticsConsent = z.infer<typeof AnalyticsConsentSchema>;

/** Whether analytics may be linked to this account: an explicit choice wins, else the country default. */
export function analyticsAllowed(consent: AnalyticsConsent | null | undefined, country: string | null | undefined): boolean {
  if (consent) return consent === 'granted';
  return !consentRequired(country);
}
